import React, { useState, useEffect, useRef, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import ReactSVG from 'react-svg';
import cx from 'classnames';
import baseService from 'services/BaseService';
import SmartPortal from '../SmartPortal';
import CommentFile from './CommentFile';
import { GET_MAX_FILE_SIZE_FROM_DICT } from 'constants/index';
import { DISPATCH_CONFIG } from './dispatchConfig';
import Loader from "../Loader"
import ModalPortal from 'components/ModalPortal';
import MessageModal from 'components/MessageModal';

import './styles.scss';

const CommentForm = (props) => {

  const { onCommentSubmitted, t, objectId, objectType, dispatch, setDispatch, onOpen, onClose, isDisabled, initCachedState = {}, onStateUpdate, isCommentFormLoader, onExistingFileDelete } = props;

  const [text, setText] = useState(initCachedState.text || "");
  const [existingFiles, setExistingFiles] = useState(
    initCachedState.files ? initCachedState.files.filter(file => !file.isTemporary) : []
  );
  const [newFiles, setNewFiles] = useState(
    initCachedState.files ? initCachedState.files.filter(file => file.isTemporary) : []
  );
  const [submitting, setSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [fullHeight, setFullHeight] = useState(initCachedState.fullHeight || false);
  const [recipientsOpen, setRecipientsOpen] = useState(initCachedState.recipientsOpen || false);
  const [recipientUsers, setRecipientUsers] = useState(initCachedState.recipientUsers || []);
  const [searchResults, setSearchResults] = useState([]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(""); 
  const [recipientEmails, setRecipientEmails] = useState(initCachedState.recipientEmails || []);
  const [emailInput, setEmailInput] = useState(""); 
  const [isFocusedCommentArea, setIsFocusedCommentArea] = useState(false);
  const [submitPath, setSubmitPath] = useState(null);
  const [fieldsDisabled, setFieldsDisabled] = useState(false);
  const [debouncedText, setDebouncedText] = useState("");
  const [fileSizeUploadError, setFileSizeUploadError] = useState(false);
  
  const commentFormFropdownRef = useRef(null);
  const inputRecipientsRef = useRef(null);
  const textareaRef = useRef(null);

  const dispatchCommentId = useMemo(() => {
    return dispatch.comment && dispatch.comment.commentId
  });

  const dispatchAction = useMemo(() => {
    return dispatch.action
  }, [dispatch]);
  
  const commentState = useMemo(() => {
    return { dispatch, text, files: [...newFiles, ...existingFiles], fullHeight, recipientsOpen, recipientUsers, recipientEmails, emailInput }
  }, [dispatch, debouncedText, newFiles, existingFiles, fullHeight, recipientsOpen, recipientUsers, recipientEmails, emailInput, searchResults]);

  const commentTypes = [
    {
      "code": "internal",
      "label": t('commentSection.internalComment'),
      "svg": "/data/svg/comments_internal.svg",
    },
    {
      "code": "public",
      "label": t('commentSection.publicReply'),
      "svg": "/data/svg/comments_public.svg",
    },
  ];

  const maxFileSize = (GET_MAX_FILE_SIZE_FROM_DICT('comment') || 10) * 1024 * 1024; // 10 MB
  
  const isSubmitDisabled = submitting || 
    (text.length === 0 && newFiles.length === 0 && existingFiles.length === 0) || 
    (dispatch.action === 'forward' && (recipientUsers.length === 0 && recipientEmails.length === 0));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
  
    const params = {
      path: `${submitPath}`,
      data: {
        commentId: dispatch.comment.commentId || null,
        text,
        objectType,
        objectId,
        publish: dispatch.commentType === 'public',
        attachment: [...existingFiles, ...newFiles].map((file) => file.id),
        receiverId: recipientUsers.map((user) => user.id),
        receiverOther: recipientEmails,
      },
      jsonType: true,
    };
  
    baseService.post('comments_new', params)
      .then((response) => {
        if (response.success) {
          resetFields();
          setDispatch(DISPATCH_CONFIG.init);
          setSubmitting(false)
          onCommentSubmitted();
        }
      })
      .catch((error) => {
        console.error('Error submitting comment:', error);
      })
  };
  
  
  const resetFields = ({ keepNewFiles = true, isCommentDeleteDispatch = false } = {}) => {
    // Delete the uploaded files if it was a new comment and form was dirty
    if (isCommentDeleteDispatch || keepNewFiles === false) {
      newFiles.forEach((file) => deleteFile(file.id, () => {}));
    }
  
    setText('');
    setExistingFiles([]);
    setNewFiles([]);
    setRecipientUsers([]);
    setRecipientEmails([]);
    setRecipientsOpen(false);
    setFieldsDisabled(false);
    setSubmitting(false);
  };
  
  const selectUser = (user) => {
    setRecipientUsers((prevUsers) => [...prevUsers, { id: user.id, name: user.userName }]);
    setSearchResults([]);
    setQuery("");
  };
  
  const toggleDropdown = () => {
    setIsDropdownOpen(prevState => !prevState);
  };

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const handleEmailInput = (e) => {
    // Check if the event is a keydown event and the key isn't "Enter"
    if (e.type === 'keydown' && e.key !== 'Enter') return;
  
    // Handle both blur and Enter events
    const inputEmails = emailInput
      .trim()
      .split(/[\s,;]+/)
      .filter((email) => validateEmail(email));
  
    setRecipientEmails((prevEmails) => [
      ...prevEmails,
      ...inputEmails.filter((email) => !prevEmails.includes(email)),
    ]);
  
    setEmailInput(""); // Clear the input field
  };
  

  const uploadFile = (file) => {

    if (file.size > maxFileSize) {
      console.error(`Attempted to upload ${file.name} which exceeds the maximum size.`);
      return; // Prevent upload if file size exceeds limit
    }

    const formData = new FormData();
    formData.append('file', file);

    const fileName = file.name;

    const params = {
      data: {
          data: {
            objectId: objectId,  
            objectType: objectType,
            fileName: fileName,
            attrCode: null,
            source: null, // 'comment' ?
          },
          file,
      },
      file: true,
   };
    // console.log("File upload params:", params);


    baseService.post("save_file", params)
      .then((response) => {
        if (response.success && response.result) {
          const id = response.result.id;
          setNewFiles((prevNewFiles) => [
            ...prevNewFiles,
            { id: id, name: file.name, file_size: file.size, mimeType: file.type, isTemporary: true },
          ]);
        } else {
          console.error('File upload failed:', response.errorMessage);
        }
      })
      .catch((error) => {
        console.error('Error during file upload:', error);
      });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
  
    selectedFiles.forEach((file) => {
      if (file.size > maxFileSize) {
        // console.error(`File ${file.name} exceeds the maximum size of ${maxFileSize} B.`);
        setFileSizeUploadError(true);
        return; // Skip this file
      }
  
      uploadFile(file); // Only upload valid files
    });
  
    // Clear the input to allow re-selecting the same file
    e.target.value = "";
  };

  const deleteFile = (id, onFinish) => {
    const params = { data: { fileId: id } };

    const isExistingFile = existingFiles.find(file => file.id === id);
    
    baseService.post("delete_file", params)
      .then((response) => {
        if (response.success) {
          setExistingFiles((prevFiles) => prevFiles.filter((file) => file.id !== id));
          setNewFiles((prevFiles) => prevFiles.filter((file) => file.id !== id));
          if (onFinish && typeof onFinish === 'function') {
            onFinish(true);
          }
          if (isExistingFile && onExistingFileDelete && typeof onExistingFileDelete === 'function') {
            onExistingFileDelete(id, dispatchCommentId);
          }
        } else {
          console.error('Error deleting file:', response.errorMessage);
          if (onFinish && typeof onFinish === 'function') {
            onFinish(false);
          }
        }
      })
      .catch((error) => {
        console.error('Error deleting file:', error);
        if (onFinish && typeof onFinish === 'function') {
          onFinish(false);
        }
      });
  };

  // Handle the paste event for files (images, PDFs, etc.)
  const handlePaste = (ev) => {
    if (!isFocusedCommentArea) return; // Only process paste if the textarea is focused

    const items = ev.clipboardData.items;

    for (const item of items) {
      if (item.kind === 'file') { // Check if the item is a file
        const file = item.getAsFile(); // Extract the file from the clipboard
        if (file) {
          uploadFile(file); // Upload the file
        }
      }
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
    }, 250);

    return () => {
      clearTimeout(handler);
    };
  }, [query]);

  useEffect(() => {
    const fetchRecipients = async () => {
      if (debouncedQuery.length === 0) {
        setSearchResults([]);
        return;
      }

      const params = {
        path: `${objectType}/${objectId}`,
        data: { query: debouncedQuery }
      };

      baseService.get("comments_new_usersearch", params)
      .then((response) => {
        if (response.success && response.result) {
          setSearchResults(response.result);
        } else {
          setSearchResults([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setSearchResults([]);
      });
    };

    fetchRecipients();
  }, [debouncedQuery]);

  useEffect(() => {
    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('paste', handlePaste); // Clean up on unmount
    };
  }, [isFocusedCommentArea]);

  useEffect(() => {
    const { action, comment, openForm, isTextareaFocused } = dispatch;

    if (action === 'delete') {
      resetFields({ isCommentDeleteDispatch: true });
    }
    
    if (onOpen && typeof onOpen === 'function' && openForm) {
      onOpen();
    }
    if (onClose && typeof onClose === 'function' && !openForm) {
      onClose();
    }

    if (openForm && isTextareaFocused && textareaRef.current) {
      textareaRef.current.focus(); // Focus the textarea when the form opens
    }
          
    if (action === 'create' || action === 'edit' || action === 'forward') {

      switch (action) {
        case 'create':
          setSubmitPath('CREATE');
          break;
        case 'edit':
          setSubmitPath('CHANGE');
          break;
        case 'forward':
          setSubmitPath('FORWARD');
          break;
        default:
          break;
      }

      if (action === 'edit' || action === 'forward') {
        const textToSet = comment.text ? comment.text : "";
        setText(initCachedState && initCachedState.text ? initCachedState.text : textToSet);
        const filesToSet = comment.attachments ? comment.attachments : [];
        setExistingFiles(initCachedState && initCachedState.files ? initCachedState.files.filter(file => !file.isTemporary) : filesToSet);
        if (newFiles.length > 0) {
          newFiles.forEach(file => deleteFile(file.id, () => {}));
        }
        setNewFiles([]);
      }

      if (action === 'forward') {
        setFieldsDisabled(true);
        setRecipientsOpen(true);
      } else {
        setFieldsDisabled(false);
      }

    }

  }, [dispatchAction, dispatchCommentId]);

  useEffect(() => {
    if (onStateUpdate && typeof onStateUpdate === 'function') {
      onStateUpdate(dispatch.openForm ? commentState : null)
    }
  }, [dispatch, commentState])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedText(text);
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [text]);

  useEffect(() => {
    if (textareaRef?.current && dispatch.openForm) {
      const resizeTextArea = () => {
        const textarea = textareaRef.current;
        if (!textarea) return; // Check if textarea is still in the DOM

        const rowHeight = parseFloat(getComputedStyle(textarea).lineHeight) || 20;
        const maxHeight = rowHeight * 16; // Max height for 16 rows

        const container = textarea.parentElement;
        const containerHeight = container ? container.clientHeight : Infinity;

        textarea.style.height = 'auto'; // Reset height to allow shrinking
        const newHeight = Math.min(textarea.scrollHeight, maxHeight, containerHeight);
        textarea.style.height = `${newHeight}px`;
      };

      resizeTextArea();
      const resizeObserver = new ResizeObserver(resizeTextArea);
      resizeObserver.observe(textareaRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [textareaRef, text]);

  return (
    <div className='comment-form'>
      {/* <div>
        <div>ID: {dispatch.comment.commentId}</div>
        <div>ACTION: {dispatch.action}</div>
        <div>TYPE: {dispatch.commentType}</div>
        <div>formOpened: {dispatch.openForm ? "true" : "false"}</div>
        <div>{files.map((file, index) => (<div key={index}>{file.name}</div>))}</div>
      </div> */}

      {!dispatch.openForm && (
        <div className='comment-form__footer'>
          <div className='comment-form__footer-title'>{t('commentSection.add')}:</div>
          <div className='comment-form__footer-buttons'>
            <button
              className="comment-form__footer-button"
              onClick={!isDisabled && (() => setDispatch(DISPATCH_CONFIG.createInternal))}
            >
              {t('commentSection.internalComment')}
            </button>
            {objectType === "INTERACTION_REQUEST" && (
              <button
                className="comment-form__footer-button"
                onClick={!isDisabled && (() => setDispatch(DISPATCH_CONFIG.createPublic))}
              >
                {t('commentSection.publicReply')}
              </button>
            )}
            {/* "File" comment type is an "internal" comment type for now */}
            <button
              className="comment-form__footer-button"
              onClick={!isDisabled && (() => setDispatch(DISPATCH_CONFIG.createInternal))}
            >
              {t('commentSection.file')}
            </button>
         </div>
        </div>
      )}

      {/* {formOpened && ( */}
      {dispatch.openForm && (
      <div className={cx("comment-form__add-wrapper", {"comment-form__add-wrapper--fullheight": fullHeight})}>
        <div className="comment-form__add">
        {isCommentFormLoader && <Loader />}
          {/* Dropdown for Comment Type */}
          <div className="comment-form__header">
              <div 
                className="comment-form__dropdown"
                onClick={objectType === "INTERACTION_REQUEST" && !fieldsDisabled ? toggleDropdown : null}
                role="button"
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
                tabIndex={0}
              >
                <div className={cx(`comment-form__dropdown-selected comment-form__dropdown-selected--${dispatch.commentType}`, {"comment-form__dropdown--open": isDropdownOpen, "comment-form__dropdown-selected--disabled": objectType !== "INTERACTION_REQUEST" || fieldsDisabled})} ref={commentFormFropdownRef}>
                  <ReactSVG className={`comment-form__dropdown__icon  ${dispatch.commentType}`} src={`${commentTypes.find(f => f.code === dispatch.commentType)?.svg}`} />
                  {
                    commentTypes.find(f => f.code === dispatch.commentType)?.label 
                  }
                  <ReactSVG className="comment-form__dropdown__icon" src={`/data/svg/dropdown.svg`} />
                </div>
                {isDropdownOpen && (
                  <SmartPortal
                    portalClassName="portal-comment-form-dropdown"
                    targetElement={commentFormFropdownRef && commentFormFropdownRef.current}
                    portalPlacement="bottom inner-left"
                    portalKey={objectId+5}
                    onClickOutside={() => setIsDropdownOpen(false)}
                    outerOffset={4}
                  >
                    <div className="comment-form__dropdown__options" role="listbox">
                      {commentTypes.map((type) => (
                        <div 
                          key={type.code} 
                          className={`comment-form__dropdown__option ${type.code === dispatch.commentType ? 'selected' : ''}`}
                          onClick={() =>
                            setDispatch((prevDispatch) => ({
                              ...prevDispatch,
                              commentType: type.code,
                            }))}
                          role="option"
                          aria-selected={type.code === dispatch.commentType}
                          tabIndex={0}
                        >
                          {type.label}
                        </div>
                      ))}
                    </div>
                  </SmartPortal>
                )}
              </div>

            {/* Icon Buttons */}
            <div className="comment-form__icons">
              <button  className={cx({"cf-disabled": fieldsDisabled})} type="button" onClick={() => document.getElementById('comment-form__file-upload').click()} disabled={fieldsDisabled}>
                <ReactSVG src="/data/svg/upload.svg" />
              </button>
              <button className={cx({"cf-activated": recipientsOpen,})} type="button" onClick={() => {setRecipientsOpen(!recipientsOpen)}}>
                <ReactSVG src="/data/svg/recipients.svg" />
              </button>
              <div className="comment-form__icons-divider"></div>
              <button className={cx({"cf-activated": fullHeight,})} type="button" onClick={() => setFullHeight(!fullHeight)}>
                <ReactSVG src="/data/svg/full-height.svg" />
              </button>
              <button type="button" onClick={() => {
                  resetFields({keepNewFiles: false});
                  setDispatch(DISPATCH_CONFIG.init);
                }}>
                <ReactSVG src="/data/svg/close.svg" />
              </button>
            </div>
          </div>

          {/* Recipient Inputs */}
          <div className={cx("comment-form__recipients", {"comment-form__recipients--open": recipientsOpen})} >
            <div className="comment-form__recipients-input">
              <ReactSVG className="comment-form__recipients-icon" src="/data/svg/comments_recipients_input.svg" />
              <div className="comment-form__recipients-input-miniwrapper">

                  {/* Display only the name */}
                  {recipientUsers.map((user) => (
                    <span
                      className="comment-form__recipients-input-label"
                      key={user.id}
                      onClick={() => setRecipientUsers(prevUsers => prevUsers.filter(u => u.id !== user.id))}
                    >
                    {user.name}
                    <button>
                      <ReactSVG src="/data/svg/close.svg" />
                    </button>
                  </span>
                ))}

                <input
                  type="text"
                  value={query}
                  placeholder={t("commentSection.searchRecipients")}
                  onChange={(e) => setQuery(e.target.value)}
                  ref={inputRecipientsRef}
                />
              </div>

              {/* Dropdown for search results */}
              {searchResults.length > 0 && (
                <SmartPortal
                  portalClassName="portal-comment-form-recipients"
                  targetElement={inputRecipientsRef && inputRecipientsRef.current}
                  portalPlacement="bottom inner-left"
                  portalKey={objectId+6}
                  onClickOutside={() => {
                      setSearchResults([]);
                      setQuery("");
                    }}
                  outerOffset={0}
                >
                  <div className="comment-form__search-dropdown">
                    {searchResults
                    .filter(user => !recipientUsers.some(selectedUser => selectedUser.id === user.id)) 
                    .map((user) => (
                      <div
                        className="comment-form__search-dropdown-item"
                        key={user.id}
                        onClick={() => selectUser(user)}
                      >
                        {user.userName} 
                      </div>
                    ))}
                  </div>
                </SmartPortal>
              )} 
            </div>

            {/* Email Inputs */}
            <div className="comment-form__recipients-input">
              <ReactSVG className="comment-form__recipients-icon" src="/data/svg/comments_email_input.svg" />
              <div className="comment-form__recipients-input-miniwrapper">
                {recipientEmails.map((email, index) => (
                  <span
                    className="comment-form__recipients-input-label"
                    key={index}
                    onClick={() => setRecipientEmails(prevEmails => prevEmails.filter(e => e !== email))}
                  >
                    {email}
                    <button>
                      <ReactSVG src="/data/svg/close.svg" />
                    </button>
                  </span>
                ))}
                <input
                  type="email"
                  value={emailInput}
                  placeholder={t('commentSection.addEmail')}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={handleEmailInput}
                  onBlur={handleEmailInput}
                />
              </div>
            </div>
          </div>

          {/* File Attachment Input */}
          <input
            type="file"
            id="comment-form__file-upload"
            name="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
            disabled={fieldsDisabled}
          />

          {/* Textarea for Comment */}
          <div className={cx("comment-form__textarea", { "comment-form__textarea--disabled": fieldsDisabled })}>
           
            {/* Display Uploaded Files */}
            {(existingFiles.length > 0 || newFiles.length > 0) && (
              <div className="comment-item__files">
                {/* {files.map((file, index) => (
                  <CommentFile key={index} file={file} removable={fieldsDisabled ? false : true} deleteFile={deleteFile}/>
                ))} */}
                {[...existingFiles, ...newFiles].map((file, index) => (
                  <CommentFile key={index} file={file} removable={fieldsDisabled ? false : true} deleteFile={deleteFile}/>
                ))}
              </div>
            )}

            <textarea
              id="comment-form-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={3000}
              placeholder={t('commentSection.writeComment')}
              disabled={fieldsDisabled}
              ref={textareaRef}
              onFocus={() => setIsFocusedCommentArea(true)}
              onBlur={() => setIsFocusedCommentArea(false)}
              onKeyDown={(e) => {
                if (e.ctrlKey && e.key === 'Enter' && !isSubmitDisabled) {
                  handleSubmit(e);
                }
              }}
            />
          </div>
          {/* {console.log("button conditions:")}
          {console.log("full",submitting || (text.length === 0 && files.length === 0))}
          {console.log("submitting", submitting)}
          {console.log("text and files",(text.length === 0 && files.length === 0))}
          {console.log("text",text.length === 0 )}
          {console.log("files",files.length === 0)} */}

          {/* Form Actions */}
          <button
            className={cx("comment-form__submit", { 'comment-form__submit--disabled': isSubmitDisabled })}
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}>
              <ReactSVG className="comment-form__submit-icon" src="/data/svg/comments_submit.svg" />
            </button>
        </div>
        {fileSizeUploadError && (
          <ModalPortal
            onClose={() => {setFileSizeUploadError(false)}}
            className='modal-small modal-center'
          > 
            <MessageModal
              danger
              titleModal={t('error')}
              contentModalText={t('errorFile', { size: maxFileSize / 1024 / 1024 })}
            /> 
          </ModalPortal>
        )}
      </div>
      )}
    </div>
  );
};

export default (withTranslation()(CommentForm));
